import { useCallback } from "react";
import { meshService } from "../lib/captainMesh/application/meshService";
import { EventTypes } from "../lib/captainMesh/domain/events";
import { uuid } from "@/lib/uuid";

/*
 * Event names come from EventTypes, never from string literals. Two of the
 * literals this file used to carry — "QUANTITY_UPDATED" and "KOT_FIRED" — are
 * not event types at all: the reducer has no case for either, so changing a
 * quantity and firing a KOT both wrote an event that every reader ignored.
 *
 * Ids come from `uuid()`, never `crypto.randomUUID`. The restaurant LAN is
 * plain HTTP, which is not a secure context, and there `crypto.randomUUID` is
 * undefined — starting an order would have thrown on the actual tablets while
 * working perfectly on localhost.
 */

export function useCaptainOrder(aggregateId) {
  
  // targetAggregateId overrides the hook's binding — conflict resolution punches
  // onto the aggregate that WON the table, and the hook was bound to the loser.
  const addItem = useCallback(async (item, variant, quantity, targetAggregateId) => {
    // The variant's name and price ride on the line itself, exactly as the
    // order-start path shapes them. Sending the base item's name/price with the
    // variant only in an id meant a "Large" punched onto an existing order
    // displayed, and reconciled, at the base price.
    return await meshService.submitEvent(targetAggregateId || aggregateId, EventTypes.ITEM_ADDED, {
      line_id: uuid(),
      menu_item_id: Number(item.id),
      name: variant ? `${item.name} · ${variant.name}` : item.name,
      price: variant ? variant.price : item.price,
      quantity: quantity || 1,
      variation_id: variant ? Number(variant.id) : null,
      modifier_ids: [],
      notes: ""
    });
  }, [aggregateId]);

  // Lines are addressed by line_id — that is the key the reducer stores them
  // under. Passing item_id matched nothing, so removals and quantity changes
  // silently did nothing.
  const removeItem = useCallback(async (lineId) => {
    return await meshService.submitEvent(aggregateId, EventTypes.ITEM_REMOVED, {
      line_id: lineId
    });
  }, [aggregateId]);

  const updateQuantity = useCallback(async (lineId, delta, targetAggregateId) => {
    return await meshService.submitEvent(targetAggregateId || aggregateId, EventTypes.ITEM_QUANTITY_DELTA, {
      line_id: lineId,
      delta: delta
    });
  }, [aggregateId]);

  // The line's special note ("extra spicy" on the dal). Only for a line no KOT
  // intent names yet — the reducer and the server both ignore it after that.
  const setNote = useCallback(async (lineId, notes, targetAggregateId) => {
    return await meshService.submitEvent(targetAggregateId || aggregateId, EventTypes.ITEM_NOTE_SET, {
      line_id: lineId,
      notes: String(notes || "").trim()
    });
  }, [aggregateId]);

  const startOrder = useCallback(async (payload) => {
    // We generate a local UUID for the aggregate until the server assigns one
    const newAggregateId = uuid();
    
    // Ensure first_items have line_ids so the reducer can map them consistently
    if (payload.first_items) {
      payload.first_items = payload.first_items.map(item => ({
        ...item,
        line_id: uuid()
      }));
    }

    return await meshService.submitEvent(newAggregateId, EventTypes.ORDER_STARTED, payload);
  }, []);

  const fireKOT = useCallback(async (lines) => {
    // The intent names its lines. Without them the reducer stored
    // kot_intents[undefined], nothing was ever marked sent, "pending" never
    // shrank, and every press re-asked the kitchen for the entire order.
    return await meshService.submitEvent(aggregateId, EventTypes.KOT_REQUESTED, {
      intent_id: uuid(),
      line_ids: (lines || []).map(l => l.line_id),
    });
  }, [aggregateId]);

  return {
    addItem,
    removeItem,
    updateQuantity,
    setNote,
    fireKOT,
    startOrder
  };
}
